import React from 'react';
import {
  User,
  CreditCard,
  Calculator,
  AlertCircle,
  CheckCircle,
  Printer,
  Edit,
  X
} from 'lucide-react';

const statusStyles = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800'
};

const ViewPaymentModal = ({
  isOpen,
  onClose,
  viewingPayment,
  onEdit,
  handlePrintPaystub,
  formatCurrency,
  formatDateDisplay,
  toTitleCase
}) => {
  if (!isOpen || !viewingPayment) {
    return null;
  }

  const paymentIdentifier = viewingPayment.paymentNumber || viewingPayment.id;
  const statusClass = statusStyles[viewingPayment.status] || 'bg-gray-100 text-gray-800';
  const payPeriod = viewingPayment.payPeriodStart && viewingPayment.payPeriodEnd
    ? `${formatDateDisplay(viewingPayment.payPeriodStart)} - ${formatDateDisplay(viewingPayment.payPeriodEnd)}`
    : 'N/A';
  const paymentDate = viewingPayment.paymentDate || viewingPayment.payDate
    ? formatDateDisplay(viewingPayment.paymentDate || viewingPayment.payDate)
    : 'N/A';
  const calculationLabel = viewingPayment.paymentCalculationType
    ? toTitleCase(viewingPayment.paymentCalculationType.replace('_', ' '))
    : 'Legacy';
  const paymentMethod = viewingPayment.paymentMethod
    ? toTitleCase(viewingPayment.paymentMethod.replace('_', ' '))
    : 'Direct Deposit';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center space-x-3">
              <h3 className="text-xl font-bold text-gray-900">Payment Details - {paymentIdentifier}</h3>
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${statusClass}`}>
                {toTitleCase(viewingPayment.status)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePrintPaystub(viewingPayment)}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={() => {
                  onClose();
                  onEdit(viewingPayment);
                }}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Recipient */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {viewingPayment.recipientName}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Type</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {toTitleCase(viewingPayment.recipientType)}
              </div>
            </div>
          </div>

          {/* General payment info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {toTitleCase(viewingPayment.paymentType?.replace('_', ' ') || 'Weekly Settlement')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {paymentMethod}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {payPeriod}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
              <div className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                {paymentDate}
              </div>
            </div>
          </div>

          {/* Calculation */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Calculator className="w-4 h-4 text-blue-500" />
                Calculation Details
              </h4>
              <span className="inline-flex px-3 py-1 text-sm font-medium rounded-full bg-white text-blue-600 border border-blue-100">
                {calculationLabel}
              </span>
            </div>

            {viewingPayment.paymentCalculationType === 'percentage' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Load Amount:</span>
                  <span className="font-medium">{formatCurrency(viewingPayment.loadAmount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Percentage:</span>
                  <span className="font-medium">{viewingPayment.percentageRate || 0}%</span>
                </div>
              </div>
            )}

            {viewingPayment.paymentCalculationType === 'flat_rate' && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Salary Amount:</span>
                <span className="font-medium">{formatCurrency(viewingPayment.salaryAmount || 0)}</span>
              </div>
            )}

            {viewingPayment.paymentCalculationType === 'mileage' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Miles:</span>
                  <span className="font-medium">{(viewingPayment.totalMiles || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rate / Mile:</span>
                  <span className="font-medium">${viewingPayment.mileageRate || 0}</span>
                </div>
              </div>
            )}

            {viewingPayment.paymentCalculationType === 'owner_operator' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Load Amount:</span>
                  <span className="font-medium">{formatCurrency(viewingPayment.loadAmount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Commission:</span>
                  <span className="font-medium text-red-600">{viewingPayment.commissionRate || 0}%</span>
                </div>
              </div>
            )}

            {!viewingPayment.paymentCalculationType && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Base Pay:</span>
                <span className="font-medium">{formatCurrency(viewingPayment.basePay || viewingPayment.grossRevenue || viewingPayment.netPayment || viewingPayment.amount || 0)}</span>
              </div>
            )}

            <div className="flex justify-between text-sm pt-4 border-t border-blue-100">
              <span className="text-gray-600">Net Amount:</span>
              <span className="font-bold text-blue-600">{formatCurrency(viewingPayment.netPayment ?? viewingPayment.amount ?? 0)}</span>
            </div>
          </div>

          {/* Deductions & bonuses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Deductions
              </h4>
              {Object.entries(viewingPayment.deductions || {}).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-red-600 capitalize">{key}:</span>
                  <span className="text-red-600">-{formatCurrency(value)}</span>
                </div>
              ))}
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Bonuses
              </h4>
              {Object.entries(viewingPayment.bonuses || {}).map(([key, value]) => (
                <div key={key} className="flex justify-between text-sm">
                  <span className="text-green-600 capitalize">{key}:</span>
                  <span className="text-green-600">+{formatCurrency(value)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => handlePrintPaystub(viewingPayment)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Print Paystub</span>
            </button>
            <button
              onClick={() => {
                onClose();
                onEdit(viewingPayment);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit Payment</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentModal;
